import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import {
  createApi,
  fetchBaseQuery,
  type FetchArgs,
} from "@reduxjs/toolkit/query/react";
import { setUser } from "@sentry/react";
import { prepareApiHeaders } from "../utilities/api";
import { posthog } from "../utilities/posthog";
import { toSearchParams } from "../utilities/searchParams";
import {
  type AcceptInviteRequest,
  type AcceptInviteSignupRequest,
  type Account,
  type AccountCountRequest,
  type AccountCountResponse,
  type AccountFilterRequest,
  type AccountInfoRequest,
  type ActiveAccountsChartRequest,
  type ActiveAccountsChartResponse,
  type AddBatteryRequest,
  type AddChargerRequest,
  type APIKey,
  type AppAccount,
  type AppAccountProgramAgreement,
  type AppConfigurationResponse,
  type AppPowerStatsRequest,
  type AppPowerStatsResponse,
  type AppRequest,
  type AppResponse,
  type AppStatsRequest,
  type AppStatsResponse,
  type ChangePasswordRequest,
  type ChatThreadBodyResponse,
  type ChatThreadDetailBodyResponse,
  type CompareAppPlanRequest,
  type CompareAppPlanResponse,
  type CompareAppRate,
  type ComparePlanRequest,
  type CreateAPIKeyRequest,
  type CreateAPIKeyResponse,
  type CreateAppRequest,
  type CreateChatThreadRequest,
  type CreateEmailValidationRequest,
  type CreateWebhookRequest,
  type CurrentActiveAccountsResponse,
  type DeleteAPIKeyRequest,
  type DeleteInviteRequest,
  type Device,
  type DeviceFilterRequest,
  type DeviceSyncRequest,
  type EmbeddedAppTestLinkRequest,
  type EmbeddedAppTestLinkResponse,
  type EnrollmentsChartRequest,
  type EnrollmentsChartResponse,
  type ExportAccountsRequest,
  type ExportAccountsResponse,
  type ExportStatusResponse,
  type ForgotPasswordRequest,
  type InviteDetailsResponse,
  type InviteUser,
  type InviteUserRequest,
  type Invoice,
  type LocationDocument,
  type LocationDocumentsRequest,
  type LocationsChartRequest,
  type LocationsChartResponse,
  type LoginRequest,
  type OrganizationUser,
  type OrganizationUserRolesUpdateRequest,
  type Page,
  type PlanDetailResponse,
  type PlanResponse,
  type PlaybookContentOverviewResponse,
  type PlaybookContentSearchResponse,
  type PlaybookFaqResponse,
  type PlaybookGuideResponse,
  type Program,
  type ProgramAgreementFilterRequest,
  type ProgramDeviceStatusCounts,
  type ResetPasswordRequest,
  type SendChatMessageRequest,
  type SimulateContractExpiringRequest,
  type StandaloneAppTestLinkResponse,
  type StandaloneResponse,
  type UpdateAccountRequest,
  type UpdateAppConfigurationFaviconLogoRequest,
  type UpdateAppConfigurationLogoRequest,
  type UpdateAppConfigurationRequest,
  type UpdateAppRequest,
  type UpdateEmailRequest,
  type UpdateOrganizationRequest,
  type UpdatePlanRequest,
  type UpdateWebhookRequest,
  type ValidateEmailRequest,
  type Webhook,
  type WebhookEvent,
  type WebhookEventRequest,
} from "./api.types";
import { local } from "./local";

export enum Tag {
  Account = "Account",
  Apps = "Apps",
  Configuration = "Configuration",
  Debug = "Debug",
  Invites = "Invites",
  Keys = "Keys",
  Standalone = "Standalone",
  Users = "Users",
  Webhooks = "Webhooks",
  Plan = "Plan",
  Plans = "Plans",
  Devices = "Devices",
  Programs = "Programs",
  AccountDevices = "AccountDevices",
  Playbook = "Playbook",
  PlaybookChat = "PlaybookChat",
}

const base = fetchBaseQuery({
  baseUrl: "/api/v1",
  paramsSerializer: (params) => toSearchParams(params).toString(),
  prepareHeaders: prepareApiHeaders,
});

const handleAccount = (
  account: Account,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
) => {
  setUser({ id: account.uuid, email: account.email });
  posthog.identify(account.uuid, {
    email: account.email,
    ...(account.organizations.length > 0 && {
      organization_uuid: account.organizations[0].uuid,
      organization_name: account.organizations[0].name,
    }),
  });
  dispatch(local.actions.handleAccount(account));
};

/**
 * The devices and program agreements for a page of accounts are fetched by
 * `account_ids`, and both of those endpoints reject more than 25 ids
 * (`MAX_ACCOUNT_IDS`). A page larger than that 400s rather than degrading, so
 * the list asks for exactly as many accounts as it can then describe.
 */
export const ACCOUNTS_PAGE_SIZE = 25;

/**
 * The devices and program agreements for a whole page of accounts are fetched
 * in one request each, so their page size has to clear the worst case of
 * several per account. The server's default of 100 would silently drop the
 * tail, leaving the last rows of the table with an empty Devices column and no
 * indication anything was missing.
 */
export const ACCOUNT_ROW_DETAIL_LIMIT = ACCOUNTS_PAGE_SIZE * 10;

export const api = createApi({
  reducerPath: "api",
  tagTypes: Object.values(Tag),
  baseQuery: async (args: string | FetchArgs, api, extraOptions) => {
    const result = await base(args, api, extraOptions);
    if (api.endpoint === "getUser" && result.error?.status === 401) {
      return { data: null, meta: result.meta };
    }
    return result;
  },
  endpoints: (build) => ({
    getUser: build.query<Account | null, null>({
      query: () => "account",
      providesTags: [Tag.Account],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            handleAccount(data, dispatch);
          }
        } catch {
          // no-op
        }
      },
    }),
    updateUser: build.mutation<Account, UpdateAccountRequest>({
      query: (body) => ({ url: "account", method: "PATCH", body }),
      invalidatesTags: [Tag.Account],
    }),
    updateEmail: build.mutation<null, UpdateEmailRequest>({
      query: (body) => ({ url: "account/update-email", method: "POST", body }),
    }),
    validateEmail: build.mutation<Account, ValidateEmailRequest>({
      query: (body) => ({
        url: "account/validate-email",
        method: "POST",
        body,
      }),
      invalidatesTags: [Tag.Account],
    }),
    cancelEmailUpdate: build.mutation<null, null>({
      query: () => ({ url: "account/email-validation", method: "DELETE" }),
      invalidatesTags: [Tag.Account],
    }),
    login: build.mutation<Account, LoginRequest>({
      query: (body) => ({ url: "account/login", method: "POST", body }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(api.util.updateQueryData("getUser", null, () => data));
          handleAccount(data, dispatch);
        } catch {
          // no-op
        }
      },
    }),
    acceptInvite: build.mutation<Account, AcceptInviteRequest>({
      query: (body) => ({ url: "account/accept-invite", method: "POST", body }),
      invalidatesTags: [Tag.Account, Tag.Apps],
    }),
    acceptInviteSignup: build.mutation<Account, AcceptInviteSignupRequest>({
      query: (body) => ({
        url: "account/accept-invite-signup",
        method: "POST",
        body,
      }),
      invalidatesTags: [Tag.Account],
    }),
    getInviteDetails: build.query<InviteDetailsResponse, string>({
      query: (inviteToken) => ({
        url: `account/invites/${inviteToken}`,
        method: "GET",
      }),
    }),
    logout: build.mutation<null, null>({
      query: () => ({ url: "account/logout", method: "POST" }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        // clear all state on logout
        await queryFulfilled.catch(() => {
          // ignore server logout errors
        });
        setUser(null);
        posthog.reset();
        dispatch(local.actions.reset());
        dispatch(api.util.resetApiState());
        localStorage.clear();
        sessionStorage.clear();
      },
    }),
    updateOrganization: build.mutation<null, UpdateOrganizationRequest>({
      query: ({ uuid, ...body }) => ({
        url: `orgs/${uuid}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [Tag.Account],
    }),
    getApps: build.query<Page<AppResponse>, string>({
      query: (orgSlug) => ({
        url: `orgs/${orgSlug}/apps`,
        params: { include: ["configuration"] },
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(local.actions.handleApps(data));
      },
      providesTags: [Tag.Apps],
    }),
    createApp: build.mutation<AppResponse, CreateAppRequest>({
      query: (body) => {
        const { orgSlug, ...rest } = body;
        return { url: `orgs/${orgSlug}/apps`, method: "POST", body: rest };
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(local.actions.set({ appSlugNext: data.slug }));
      },
      invalidatesTags: [Tag.Apps],
    }),
    updateApp: build.mutation<AppResponse, UpdateAppRequest>({
      query: ({ uuid, ...body }) => ({
        url: `apps/${uuid}`,
        method: "PATCH",
        body,
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(local.actions.set({ appSlugNext: data.slug }));
      },
      invalidatesTags: [Tag.Apps],
    }),
    getAppPlans: build.query<Page<PlanResponse>, string>({
      query: (appUUID) => ({
        url: `apps/${encodeURIComponent(appUUID)}/plans`,
        params: { limit: 100 },
      }),
      providesTags: [Tag.Plans],
    }),
    getAppPrograms: build.query<Page<Program>, string>({
      query: (appUUID) => ({
        url: `apps/${encodeURIComponent(appUUID)}/programs`,
        params: { limit: 100 },
      }),
      providesTags: [Tag.Programs],
    }),
    getProgram: build.query<Program, string>({
      query: (uuid) => `programs/${encodeURIComponent(uuid)}`,
      providesTags: [Tag.Programs],
    }),
    getProgramDeviceStats: build.query<ProgramDeviceStatusCounts, string>({
      query: (uuid) =>
        `/programs/${encodeURIComponent(uuid)}/stats/device-counts`,
    }),
    getPlan: build.query<PlanDetailResponse, string>({
      query: (uuid) => `plans/${encodeURIComponent(uuid)}`,
      providesTags: [Tag.Plan],
    }),
    getPlanEFLs: build.query<{ blob: Blob; filename: string }, string>({
      query: (uuid) => ({
        url: `plans/${encodeURIComponent(uuid)}/efl`,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get(
            "Content-Disposition",
          );
          const filenameMatch = contentDisposition?.match(
            /filename="?([^";\n]+)"?/,
          );
          const filename = filenameMatch?.[1] ?? "efls.zip";
          return { blob, filename };
        },
      }),
    }),
    getPlanRates: build.query<CompareAppRate[], string>({
      query: (uuid) => `plans/${encodeURIComponent(uuid)}/rates`,
    }),
    comparePlan: build.mutation<CompareAppPlanResponse, ComparePlanRequest>({
      query: ({ planUUID, ...body }) => ({
        url: `plans/${planUUID}/compare`,
        method: "POST",
        body,
      }),
    }),
    updatePlan: build.mutation<PlanResponse, UpdatePlanRequest>({
      query: ({ planUUID, ...body }) => ({
        url: `plans/${planUUID}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [Tag.Plan, Tag.Plans],
    }),
    compareAppPlan: build.mutation<
      CompareAppPlanResponse,
      CompareAppPlanRequest
    >({
      query: ({ appUUID, ...body }) => ({
        url: `apps/${appUUID}/plans/compare`,
        method: "POST",
        body,
      }),
    }),
    getMembers: build.query<Page<OrganizationUser>, string>({
      query: (orgSlug) => `orgs/${orgSlug}/users`,
      providesTags: [Tag.Users],
    }),
    inviteMember: build.mutation<null, InviteUserRequest>({
      query: (body) => {
        const { orgSlug, ...rest } = body;
        return { url: `orgs/${orgSlug}/invites`, method: "POST", body: rest };
      },
      invalidatesTags: [Tag.Invites],
    }),
    deleteInvite: build.mutation<null, DeleteInviteRequest>({
      query: (body) => {
        const { orgSlug, email } = body;
        return {
          url: `orgs/${orgSlug}/invites`,
          method: "DELETE",
          body: { email },
        };
      },
      invalidatesTags: [Tag.Invites],
    }),
    getInvites: build.query<Page<InviteUser>, string>({
      query: (orgSlug) => `orgs/${orgSlug}/invites`,
      providesTags: [Tag.Invites],
    }),
    changePassword: build.mutation<null, ChangePasswordRequest>({
      query: (body) => ({
        url: "account/change-password",
        method: "POST",
        body,
      }),
    }),
    forgotPassword: build.mutation<null, ForgotPasswordRequest>({
      query: (body) => ({
        url: "account/forgot-password",
        method: "POST",
        body,
      }),
    }),
    resetPassword: build.mutation<null, ResetPasswordRequest>({
      query: (body) => ({
        url: "account/reset-password",
        method: "POST",
        body,
      }),
      invalidatesTags: [Tag.Account],
    }),
    removeOrgMember: build.mutation<null, string>({
      query: (userUUID) => ({ url: `org-users/${userUUID}`, method: "DELETE" }),
      invalidatesTags: [Tag.Users],
    }),
    syncUserRoles: build.mutation<null, OrganizationUserRolesUpdateRequest>({
      query: (body) => {
        const { userUUID, roles } = body;
        return {
          url: `org-users/${userUUID}/sync-roles`,
          method: "POST",
          body: { roles },
        };
      },
      invalidatesTags: [Tag.Users],
    }),
    createEmailValidation: build.mutation<null, CreateEmailValidationRequest>({
      query: (body) => ({
        url: "account/email-validation",
        method: "POST",
        body,
      }),
    }),
    getApiKeys: build.query<Page<APIKey>, string>({
      query: (appUUID) => `apps/${appUUID}/api-keys`,
      providesTags: [Tag.Keys],
    }),
    createAPIKey: build.mutation<CreateAPIKeyResponse, CreateAPIKeyRequest>({
      query: ({ appUUID, name }) => ({
        url: `apps/${appUUID}/api-keys`,
        method: "POST",
        body: { name },
      }),
      invalidatesTags: [Tag.Keys],
    }),
    deleteAPIKey: build.mutation<null, DeleteAPIKeyRequest>({
      query: ({ appUUID, clientID }) => ({
        url: `apps/${appUUID}/api-keys/${clientID}`,
        method: "DELETE",
      }),
      invalidatesTags: [Tag.Keys],
    }),
    getWebhooks: build.query<Page<Webhook>, string>({
      query: (appUUID) => `apps/${appUUID}/webhooks`,
      providesTags: [Tag.Webhooks],
    }),
    createWebhook: build.mutation<Webhook, CreateWebhookRequest>({
      query: (body) => {
        const { appUUID, ...rest } = body;
        return { url: `apps/${appUUID}/webhooks`, method: "POST", body: rest };
      },
      invalidatesTags: [Tag.Webhooks],
    }),
    updateWebhook: build.mutation<Webhook, UpdateWebhookRequest>({
      query: ({ uuid, ...body }) => ({
        url: `webhooks/${uuid}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [Tag.Webhooks],
    }),
    deleteWebhook: build.mutation<null, string>({
      query: (webhookUUID) => ({
        url: `webhooks/${webhookUUID}`,
        method: "DELETE",
      }),
      invalidatesTags: [Tag.Webhooks],
    }),
    getWebhookEvents: build.query<Page<WebhookEvent>, WebhookEventRequest>({
      query: ({ webhookUUID, offset }) => ({
        method: "GET",
        url: `webhooks/${webhookUUID}/events`,
        params: { limit: 25, offset },
      }),
    }),
    getConfiguration: build.query<AppConfigurationResponse | null, string>({
      query: (appUUID) => ({
        url: `apps/${appUUID}/configuration`,
        method: "GET",
      }),
      providesTags: [Tag.Configuration],
    }),
    updateConfiguration: build.mutation<
      AppConfigurationResponse,
      UpdateAppConfigurationRequest
    >({
      query: ({ appUUID, ...body }) => ({
        url: `apps/${appUUID}/configuration`,
        method: "POST",
        body,
      }),
      invalidatesTags: [Tag.Configuration, Tag.Apps],
    }),
    updateConfigurationLogo: build.mutation<
      AppConfigurationResponse,
      UpdateAppConfigurationLogoRequest
    >({
      query: ({ appUUID, logo }) => {
        const formData = new FormData();
        formData.append("file", logo);
        return {
          url: `apps/${appUUID}/configuration/logo`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [Tag.Configuration, Tag.Apps],
    }),
    updateConfigurationFaviconLogo: build.mutation<
      AppConfigurationResponse,
      UpdateAppConfigurationFaviconLogoRequest
    >({
      query: ({ appUUID, favicon_logo }) => {
        const formData = new FormData();
        formData.append("file", favicon_logo);
        return {
          url: `apps/${appUUID}/configuration/favicon-logo`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [Tag.Configuration, Tag.Apps],
    }),
    createEmbeddedAppTestLink: build.mutation<
      EmbeddedAppTestLinkResponse,
      EmbeddedAppTestLinkRequest
    >({
      query: ({ appUUID, ...body }) => ({
        url: `apps/${appUUID}/embedded-test-link`,
        method: "POST",
        body,
      }),
    }),
    getStandalone: build.query<StandaloneResponse | null, string>({
      query: (appUUID) => ({
        url: `apps/${appUUID}/standalone`,
        method: "GET",
      }),
      providesTags: [Tag.Standalone],
    }),
    createStandaloneAppTestLink: build.mutation<
      StandaloneAppTestLinkResponse,
      string
    >({
      query: (appUUID) => ({
        url: `apps/${appUUID}/standalone-test-link`,
        method: "POST",
      }),
    }),
    getAccounts: build.query<Page<AppAccount>, AccountFilterRequest>({
      query: ({
        appUUID,
        filter,
        search,
        offset,
        plan_group_uuid,
        plan_type,
        include_fallback_plans,
        device_type,
        device_status,
        missing_program_device,
        expiring_in_days,
      }) => {
        return {
          url: `public-api/app/accounts`,
          params: {
            filter,
            search,
            limit: ACCOUNTS_PAGE_SIZE,
            offset,
            plan_group_uuid,
            plan_type,
            include_fallback_plans,
            device_type,
            device_status,
            missing_program_device,
            expiring_in_days,
          },
          method: "GET",
          headers: { "X-App-UUID": appUUID },
        };
      },
    }),
    getProgramsAgreements: build.query<
      Page<AppAccountProgramAgreement>,
      ProgramAgreementFilterRequest
    >({
      query: ({ appUUID, offset, account_ids }) => ({
        url: `public-api/app/programs/agreements`,
        params: { offset, account_ids, limit: ACCOUNT_ROW_DETAIL_LIMIT },
        method: "GET",
        headers: { "X-App-UUID": appUUID },
      }),
    }),
    getAccountProgramAgreements: build.query<
      Page<AppAccountProgramAgreement>,
      AccountInfoRequest
    >({
      query: ({ appUUID, accountUUID }) => {
        return {
          url: `public-api/app/accounts/${accountUUID}/programs/agreements`,
          method: "GET",
          headers: { "X-App-UUID": appUUID },
        };
      },
    }),
    getDevices: build.query<Page<Device>, DeviceFilterRequest>({
      query: ({ appUUID, offset, account_ids }) => ({
        url: `public-api/app/devices`,
        params: { offset, account_ids, limit: ACCOUNT_ROW_DETAIL_LIMIT },
        method: "GET",
        headers: { "X-App-UUID": appUUID },
      }),
      providesTags: [Tag.Devices],
    }),
    getAccountDevices: build.query<Page<Device>, AccountInfoRequest>({
      query: ({ appUUID, accountUUID }) => ({
        url: `public-api/app/accounts/${accountUUID}/devices`,
        method: "GET",
        headers: { "X-App-UUID": appUUID },
      }),
      providesTags: [Tag.AccountDevices],
    }),
    syncDevice: build.mutation<Device, DeviceSyncRequest>({
      query: ({ appUUID, accountUUID, deviceUUID }) => ({
        url: `public-api/app/accounts/${accountUUID}/devices/${deviceUUID}/sync`,
        method: "POST",
        headers: { "X-App-UUID": appUUID },
      }),
      invalidatesTags: [Tag.Devices],
    }),
    addBattery: build.mutation<Device, AddBatteryRequest>({
      query: ({
        appUUID,
        accountUUID,
        manufacturer,
        identifier,
        preliminary_capacity_kwh,
      }) => ({
        url: `public-api/app/accounts/${accountUUID}/devices/battery`,
        method: "POST",
        headers: { "X-App-UUID": appUUID },
        body: { manufacturer, identifier, preliminary_capacity_kwh },
      }),
      invalidatesTags: [Tag.Devices, Tag.AccountDevices],
    }),
    addCharger: build.mutation<Device, AddChargerRequest>({
      query: ({ appUUID, accountUUID, manufacturer, identifier }) => ({
        url: `public-api/app/accounts/${accountUUID}/devices/charger`,
        method: "POST",
        headers: { "X-App-UUID": appUUID },
        body: { manufacturer, identifier },
      }),
      invalidatesTags: [Tag.Devices, Tag.AccountDevices],
    }),
    getAccountsCount: build.query<AccountCountResponse, AccountCountRequest>({
      query: ({
        appUUID,
        filter,
        search,
        plan_group_uuid,
        plan_type,
        include_fallback_plans,
        device_type,
        device_status,
        missing_program_device,
        expiring_in_days,
      }) => {
        return {
          url: `public-api/app/accounts/count`,
          params: {
            filter,
            search,
            plan_group_uuid,
            plan_type,
            include_fallback_plans,
            device_type,
            device_status,
            missing_program_device,
            expiring_in_days,
          },
          method: "GET",
          headers: { "X-App-UUID": appUUID },
        };
      },
    }),
    getAccount: build.query<AppAccount, AccountInfoRequest>({
      query: ({ appUUID, accountUUID }) => ({
        url: `public-api/app/accounts/${accountUUID}`,
        method: "GET",
        headers: { "X-App-UUID": appUUID },
      }),
    }),
    getInvoices: build.query<Page<Invoice>, AccountInfoRequest>({
      query: ({ appUUID, accountUUID }) => ({
        url: "public-api/app/account/billing/invoices",
        params: { limit: 25, offset: 0 },
        method: "GET",
        headers: { "X-App-UUID": appUUID, "X-Account-UUID": accountUUID },
      }),
    }),
    getLocationDocuments: build.query<
      Page<LocationDocument>,
      LocationDocumentsRequest
    >({
      query: ({ appUUID, accountUUID, locationUUID }) => ({
        url: `public-api/app/account/locations/${locationUUID}/all-documents`,
        params: { limit: 25, offset: 0 },
        method: "GET",
        headers: { "X-App-UUID": appUUID, "X-Account-UUID": accountUUID },
      }),
    }),
    getDebugAccount: build.query<AppAccount, AppRequest>({
      query: ({ appUUID }) => {
        return {
          url: `public-api/app/account`,
          method: "GET",
          headers: { "X-App-UUID": appUUID },
        };
      },
      providesTags: [Tag.Debug],
    }),
    processEnrollment: build.mutation<AppAccount, AppRequest>({
      query: ({ appUUID }) => {
        return {
          url: `public-api/app/account/sandbox/seed-demo-data`,
          method: "POST",
          headers: { "X-App-UUID": appUUID },
        };
      },
      invalidatesTags: [Tag.Debug],
    }),
    deleteEnrollment: build.mutation<AppAccount, AppRequest>({
      query: ({ appUUID }) => {
        return {
          url: `public-api/app/account/sandbox/delete-enrollment`,
          method: "POST",
          headers: { "X-App-UUID": appUUID },
        };
      },
      invalidatesTags: [Tag.Debug],
    }),
    simulateServiceDrop: build.mutation<AppAccount, AppRequest>({
      query: ({ appUUID }) => {
        return {
          url: `public-api/app/account/sandbox/simulate-service-drop`,
          method: "POST",
          headers: { "X-App-UUID": appUUID },
        };
      },
      invalidatesTags: [Tag.Debug],
    }),
    simulateContractExpiring: build.mutation<
      AppAccount,
      SimulateContractExpiringRequest
    >({
      query: ({ appUUID, days_before_expiration }) => {
        return {
          url: `public-api/app/account/sandbox/simulate-contract-expiring`,
          method: "POST",
          headers: { "X-App-UUID": appUUID },
          body: { days_before_expiration },
        };
      },
      invalidatesTags: [Tag.Debug],
    }),
    exportAccounts: build.mutation<
      ExportAccountsResponse,
      ExportAccountsRequest
    >({
      query: ({ appUUID, filter, search }) => {
        return {
          url: `apps/${appUUID}/export/accounts`,
          method: "POST",
          params: { filter, search },
        };
      },
    }),
    getExport: build.query<
      ExportStatusResponse,
      { exportUUID: string; appUUID: string }
    >({
      query: ({ exportUUID }) => ({
        url: `export/${exportUUID}`,
        method: "GET",
      }),
    }),
    getAppStats: build.query<AppStatsResponse, AppStatsRequest>({
      query: ({ appUUID, days = 30 }) => ({
        url: `apps/${encodeURIComponent(appUUID)}/stats`,
        params: { days },
      }),
    }),
    getAppPowerStats: build.query<AppPowerStatsResponse, AppPowerStatsRequest>({
      query: ({ appUUID }) => ({
        url: `apps/${encodeURIComponent(appUUID)}/stats/power`,
      }),
    }),
    getAppLocations: build.query<LocationsChartResponse, LocationsChartRequest>(
      {
        query: ({ appUUID }) => ({
          url: `apps/${encodeURIComponent(appUUID)}/stats/locations`,
        }),
      },
    ),
    getActiveAccountsChart: build.query<
      ActiveAccountsChartResponse,
      ActiveAccountsChartRequest
    >({
      query: ({ appUUID }) => ({
        url: `apps/${encodeURIComponent(appUUID)}/stats/active-accounts`,
      }),
    }),
    getEnrollmentsChart: build.query<
      EnrollmentsChartResponse,
      EnrollmentsChartRequest
    >({
      query: ({ appUUID, days = 30 }) => ({
        url: `apps/${encodeURIComponent(appUUID)}/stats/enrollments`,
        params: { days },
      }),
    }),
    getCurrentActiveAccounts: build.query<
      CurrentActiveAccountsResponse,
      string
    >({
      query: (appUUID) => ({
        url: `apps/${encodeURIComponent(appUUID)}/stats/current-active-accounts`,
      }),
    }),
    getPlaybookContentOverview: build.query<
      PlaybookContentOverviewResponse,
      null
    >({
      query: () => ({
        url: `playbook/content/overview`,
      }),
      providesTags: [Tag.Playbook],
    }),
    getPlaybookContentGuide: build.query<PlaybookGuideResponse, string>({
      query: (slug) => ({
        url: `playbook/content/guides/${slug}`,
      }),
      providesTags: [Tag.Playbook],
    }),
    getPlaybookContentFaq: build.query<PlaybookFaqResponse, string>({
      query: (slug) => ({
        url: `playbook/content/faqs/${slug}`,
      }),
      providesTags: [Tag.Playbook],
    }),
    getPlaybookContentSearch: build.query<
      PlaybookContentSearchResponse,
      string
    >({
      query: (query) => ({
        url: `playbook/content/search`,
        params: { query },
      }),
      providesTags: [Tag.Playbook],
    }),
    createChatThread: build.mutation<
      ChatThreadBodyResponse,
      CreateChatThreadRequest
    >({
      query: (arg) => ({
        url: `playbook/chat/threads`,
        method: "POST",
        body: { title: arg.title },
      }),
      invalidatesTags: [Tag.PlaybookChat],
    }),
    getChatThread: build.query<ChatThreadDetailBodyResponse, string>({
      query: (threadUUID) => ({
        url: `playbook/chat/threads/${encodeURIComponent(threadUUID)}`,
      }),
      providesTags: [Tag.PlaybookChat],
    }),
    sendChatMessage: build.mutation<null, SendChatMessageRequest>({
      query: (arg) => ({
        url: `playbook/chat/threads/${encodeURIComponent(arg.threadUUID)}/messages`,
        method: "POST",
        body: { content: arg.content },
      }),
      invalidatesTags: [Tag.PlaybookChat],
    }),
  }),
});
